import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { toast } from 'sonner'
import { AuthLayout } from '@/layout/AuthLayout'

export function VerifyEmail() {
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const { user, authService } = useAuth()
  const navigate = useNavigate()

  if (!user?.email) {
    navigate('/')
    return null
  }

  const handleSendCode = async () => {
    setIsSending(true)
    try {
      await authService.sendVerificationCode()
      toast.success('Verification code sent to your email!')
    } catch (error) {
      toast.error('Failed to send verification code')
    } finally {
      setIsSending(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await authService.verifyEmail(code)
      toast.success('Email verified successfully!')
      navigate('/')
    } catch (error) {
      toast.error(
        'Failed to verify email. Please check the code and try again.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card className="border-0 shadow-none">
        <CardHeader className="pb-4">
          <CardTitle className="text-2xl py-4">Verify Your Email</CardTitle>
          <CardDescription className="text-xs text-slate-600 dark:text-slate-400">
            Enter the verification code sent to {user.email}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Input
                id="code"
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                required
                placeholder="Enter the verification code"
                className="rounded-xl shadow-xl"
              />
            </div>
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-xl shadow-xl"
                onClick={handleSendCode}
                disabled={isSending}
              >
                {isSending ? 'Sending...' : 'Send Verification Code'}
              </Button>
              <Button
                type="submit"
                className="w-full rounded-xl shadow-xl mt-4"
                disabled={isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify Email'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
